"use client"

/**
 * 5-step "before you seed" prep checklist for /tools/seeding-calculator.
 * Replaces a wall of prose with a scannable card grid. Copy mirrors the
 * Claude Design polish for this tool.
 */

import type { ReactNode } from "react"

const S_INK = "#111827"
const S_INK2 = "#374151"
const S_RULE = "#e5e7eb"
const S_BRAND = "#16a34a"
const S_BRAND_700 = "#15803d"
const S_BRAND_50 = "#f0fdf4"

interface PrepStep {
  n: number
  title: string
  blurb: string
  icon: ReactNode
}

const PREP_STEPS: PrepStep[] = [
  {
    n: 1,
    title: "Test the soil",
    blurb:
      "Aim for pH 6.0–7.0 for most grasses. Cheap home test kit is fine; add lime if it’s low.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 3v8l-4 7a4 4 0 007 4 4 4 0 007-4l-4-7V3" />
        <path d="M9 3h6" />
      </svg>
    ),
  },
  {
    n: 2,
    title: "Clear and loosen",
    blurb:
      "Pull dead grass and weeds. Rake or scarify the top ¼ inch — seed needs soil contact, not thatch.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 21h18" />
        <path d="M7 21v-6M11 21v-9M15 21v-6M19 21v-9" />
        <path d="M5 15l14-6" />
      </svg>
    ),
  },
  {
    n: 3,
    title: "Spread at the right rate",
    blurb:
      "Split the seed in half: walk north–south, then east–west. Stops striping and patchiness.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="8" />
        <circle cx="8" cy="10" r="1" fill="currentColor" />
        <circle cx="14" cy="9" r="1" fill="currentColor" />
        <circle cx="11" cy="14" r="1" fill="currentColor" />
        <circle cx="16" cy="13" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    n: 4,
    title: "Top-dress lightly",
    blurb:
      "Cover with ¼ inch of peat or screened compost. Holds moisture so seed doesn’t cook on the surface.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 16h18" />
        <path d="M5 16c2-2 4-2 6 0s4 2 6 0 2 0 2 0" />
        <path d="M8 12v-2M12 11V8M16 12v-2" />
      </svg>
    ),
  },
  {
    n: 5,
    title: "Water 2–3× per day",
    blurb:
      "Keep the top inch damp for the first two weeks. Short bursts, not deep soaks, until germination.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3s5 5 5 9a5 5 0 11-10 0c0-4 5-9 5-9z" />
        <path d="M9 14a3 3 0 003 3" />
      </svg>
    ),
  },
]

export function SeedingPrepChecklist() {
  return (
    <section
      aria-labelledby="seeding-prep-heading"
      style={{ marginTop: 40, color: S_INK }}
    >
      <div style={{ marginBottom: 20, maxWidth: 720 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: S_BRAND_700,
            marginBottom: 8,
          }}
        >
          Before you seed
        </div>
        <h2
          id="seeding-prep-heading"
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            color: S_INK,
          }}
        >
          A 5-step prep checklist
        </h2>
        <p style={{ margin: "8px 0 0", fontSize: 15, color: S_INK2, lineHeight: 1.6 }}>
          Most failed seedings trace back to step 2 or step 5. The window only works if the
          bed is ready and the soil stays damp.
        </p>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        {PREP_STEPS.map((s) => (
          <div
            key={s.n}
            style={{
              background: "#fff",
              border: `1px solid ${S_RULE}`,
              borderRadius: 14,
              padding: "18px 18px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  background: S_BRAND_50,
                  color: S_BRAND,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                {s.icon}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: S_BRAND_700,
                }}
              >
                Step {s.n}
              </span>
            </div>
            <div>
              <div
                style={{ fontSize: 16, fontWeight: 600, color: S_INK, marginBottom: 4 }}
              >
                {s.title}
              </div>
              <div style={{ fontSize: 13.5, color: S_INK2, lineHeight: 1.55 }}>
                {s.blurb}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
